import React, { useState, useEffect, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { AlertCircle } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import { GAME_TYPES } from '../../utils/constants';

const LiveScoreView = () => {
  const { activeGame, setActiveGame, users, currentUser, setView, setSelectedGameId, events, showConfirm, showAlert } = useContext(AppContext);
  const [currentHole, setCurrentHole] = useState(activeGame?.currentHole || 1);

  // Real-time listener so all players see score updates from others
  useEffect(() => {
    if (!activeGame?.id) return;
    const unsub = firestoreService.onGameChange(activeGame.id, (updatedGame) => {
      setActiveGame(updatedGame);
      if (updatedGame.status === 'completed') {
        setSelectedGameId(updatedGame.id);
        setView('game-recap');
      }
    });
    return () => unsub();
  }, [activeGame?.id, setActiveGame, setSelectedGameId, setView]);

  if (!activeGame) return null;

  const isOwner = activeGame.createdBy === currentUser.id;
  const canEditPlayer = (playerId) => isOwner || playerId === currentUser.id;
  const gameTypeInfo = GAME_TYPES.find(g => g.id === activeGame.gameType);
  const holes = activeGame.holes || {};
  const holeData = holes[String(currentHole)] || { par: 4, scores: {} };

  const updateScore = async (userId, delta) => {
    const currentScore = holeData.scores[userId] || 0;
    const newScore = Math.max(0, currentScore + delta);
    const updatedHoles = {
      ...holes,
      [String(currentHole)]: {
        ...holeData,
        scores: { ...holeData.scores, [userId]: newScore }
      }
    };

    const updatedGame = { ...activeGame, holes: updatedHoles, currentHole };
    setActiveGame(updatedGame);

    try {
      // Field-path updates so concurrent writes from multiple players don't overwrite each other.
      // Also seed par for newly-touched holes so other players see a sensible default.
      await firestoreService.updateGame(activeGame.id, {
        [`holes.${currentHole}.par`]: holeData.par,
        [`holes.${currentHole}.scores.${userId}`]: newScore,
        currentHole,
      });
    } catch (err) {
      console.error('Error updating score:', err);
    }
  };

  const updatePar = async (newPar) => {
    const updatedHoles = {
      ...holes,
      [String(currentHole)]: { ...holeData, par: newPar }
    };
    const updatedGame = { ...activeGame, holes: updatedHoles };
    setActiveGame(updatedGame);
    try {
      await firestoreService.updateGame(activeGame.id, {
        [`holes.${currentHole}.par`]: newPar,
      });
    } catch (err) {
      console.error('Error updating par:', err);
    }
  };

  const getTotalScore = (userId) => {
    return Object.values(holes).reduce((sum, h) => sum + (h.scores?.[userId] || 0), 0);
  };

  const getTotalPar = () => {
    return Object.values(holes).reduce((sum, h) => sum + (h.par || 4), 0);
  };

  // Skins calculation
  const getSkinWinner = (holeNum) => {
    const h = holes[String(holeNum)];
    if (!h?.scores || Object.keys(h.scores).length === 0) return null;
    const scores = Object.entries(h.scores).filter(([, s]) => s > 0);
    if (scores.length === 0) return null;
    const minScore = Math.min(...scores.map(([, s]) => s));
    const winners = scores.filter(([, s]) => s === minScore);
    return winners.length === 1 ? winners[0][0] : null; // null if tie (carryover)
  };

  // Nassau standings
  const getNassauStandings = () => {
    const front = {}, back = {}, overall = {};
    activeGame.players.forEach(p => { front[p.userId] = 0; back[p.userId] = 0; overall[p.userId] = 0; });
    for (let i = 1; i <= activeGame.totalHoles; i++) {
      const h = holes[String(i)];
      if (!h?.scores) continue;
      activeGame.players.forEach(p => {
        const s = h.scores[p.userId] || 0;
        if (s > 0) {
          overall[p.userId] += s;
          if (i <= 9) front[p.userId] += s;
          else back[p.userId] += s;
        }
      });
    }
    return { front, back, overall };
  };

  // Press detection
  const checkPressAlert = () => {
    if (activeGame.gameType !== 'press' || activeGame.players.length < 2) return null;
    const p1 = activeGame.players[0]?.userId;
    const p2 = activeGame.players[1]?.userId;
    let p1Wins = 0, p2Wins = 0;
    for (let i = 1; i <= currentHole; i++) {
      const h = holes[String(i)];
      if (!h?.scores) continue;
      const s1 = h.scores[p1] || 0, s2 = h.scores[p2] || 0;
      if (s1 > 0 && s2 > 0) {
        if (s1 < s2) p1Wins++;
        else if (s2 < s1) p2Wins++;
      }
    }
    const diff = Math.abs(p1Wins - p2Wins);
    if (diff >= 2) {
      const trailing = p1Wins < p2Wins ? activeGame.players[0]?.name : activeGame.players[1]?.name;
      return `${trailing} is down by ${diff} — Press available!`;
    }
    return null;
  };

  const handleCompleteGame = async () => {
    if (!await showConfirm('Complete this game?', 'Complete Game', 'Complete')) return;
    try {
      const completedAt = new Date().toISOString();
      await firestoreService.completeGame(activeGame.id);
      // Mark event as ended and set date to today
      if (activeGame.eventId) {
        const today = new Date().toISOString().split('T')[0];
        await firestoreService.updateEvent(activeGame.eventId, { date: today, status: 'ended', endedAt: completedAt });
      }
      setSelectedGameId(activeGame.id);
      setActiveGame({ ...activeGame, status: 'completed', completedAt });
      setView('game-recap');
    } catch (err) {
      console.error('Error completing game:', err);
    }
  };

  const handlePauseGame = async () => {
    try {
      await firestoreService.pauseGame(activeGame.id, currentHole);
      setActiveGame(null);
      setView('my-scorecards');
    } catch (err) {
      console.error('Error pausing game:', err);
      showAlert('Failed to pause game.', 'Error');
    }
  };

  const pressAlert = checkPressAlert();

  return (
    <div className="max-w-2xl mx-auto p-4">
      {/* Header */}
      <div className="bg-black rounded-lg border border-gray-700 p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <span className="text-2xl">{gameTypeInfo?.emoji}</span>
            <span className="text-white font-bold text-lg">{gameTypeInfo?.name}</span>
          </div>
          <span className="text-sm text-gray-400">{activeGame.courseName}</span>
        </div>

        {/* Hole Navigator */}
        <div className="flex items-center justify-center space-x-6 my-4">
          <button onClick={() => setCurrentHole(Math.max(1, currentHole - 1))} disabled={currentHole <= 1} className="w-12 h-12 rounded-full bg-gray-800 text-white text-2xl font-bold flex items-center justify-center disabled:opacity-30 active:bg-gray-600">
            &lt;
          </button>
          <div className="text-center">
            <p className="text-3xl font-bold text-white">Hole {currentHole}</p>
            <p className="text-sm text-gray-400">of {activeGame.totalHoles}</p>
          </div>
          <button onClick={() => setCurrentHole(Math.min(activeGame.totalHoles, currentHole + 1))} disabled={currentHole >= activeGame.totalHoles} className="w-12 h-12 rounded-full bg-gray-800 text-white text-2xl font-bold flex items-center justify-center disabled:opacity-30 active:bg-gray-600">
            &gt;
          </button>
        </div>

        {/* Par Selector */}
        <div className="flex items-center justify-center space-x-2 mb-2">
          <span className="text-gray-400 text-sm">Par:</span>
          {[3, 4, 5].map(p => (
            <button key={p} onClick={() => isOwner && updatePar(p)} disabled={!isOwner} className={`w-10 h-10 rounded-full text-lg font-bold ${holeData.par === p ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-white'} ${!isOwner ? 'cursor-not-allowed opacity-60' : ''}`}>
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Press Alert */}
      {pressAlert && (
        <div className="bg-yellow-900 border border-yellow-600 rounded-lg p-3 mb-4 flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 text-yellow-400" />
          <span className="text-yellow-200 text-sm font-medium">{pressAlert}</span>
        </div>
      )}

      {/* Score Entry - Large glove-friendly buttons */}
      <div className="space-y-3 mb-4">
        {activeGame.players.map(player => {
          const score = holeData.scores[player.userId] || 0;
          const total = getTotalScore(player.userId);
          const u = users.find(usr => usr.id === player.userId);
          return (
            <div key={player.userId} className="bg-black rounded-lg border border-gray-700 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  {u && <img src={u.profilePicture} alt={player.name} className="w-8 h-8 rounded-full" />}
                  <span className="text-white font-bold">{player.name}</span>
                </div>
                <span className="text-gray-400 text-sm">Total: {total}</span>
              </div>
              <div className="flex items-center justify-center space-x-6">
                <button onClick={() => updateScore(player.userId, -1)} disabled={!canEditPlayer(player.userId)} className={`w-16 h-16 rounded-full text-3xl font-bold flex items-center justify-center shadow-lg ${canEditPlayer(player.userId) ? 'bg-red-600 text-pure-white active:bg-red-700' : 'bg-gray-700 text-gray-300 cursor-not-allowed'}`}>
                  -
                </button>
                <div className="text-center min-w-[80px]">
                  <p className={`text-5xl font-bold ${score === 0 ? 'text-gray-500' : score <= holeData.par ? 'text-green-400' : score > holeData.par + 1 ? 'text-red-400' : 'text-yellow-400'}`}>
                    {score || '-'}
                  </p>
                  {score > 0 && (
                    <p className="text-xs text-gray-400 mt-1">
                      {score - holeData.par === 0 ? 'Par' : score - holeData.par < 0 ? `${score - holeData.par}` : `+${score - holeData.par}`}
                    </p>
                  )}
                </div>
                <button onClick={() => updateScore(player.userId, 1)} disabled={!canEditPlayer(player.userId)} className={`w-16 h-16 rounded-full text-3xl font-bold flex items-center justify-center shadow-lg ${canEditPlayer(player.userId) ? 'bg-[#16a34a] text-pure-white active:bg-[#15803d]' : 'bg-gray-700 text-gray-300 cursor-not-allowed'}`}>
                  +
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Game-specific displays */}
      {activeGame.gameType === 'skins' && (
        <div className="bg-black rounded-lg border border-gray-700 p-4 mb-4">
          <h4 className="text-white font-bold mb-2">Skins</h4>
          <div className="grid grid-cols-3 gap-2">
            {Array.from({ length: Math.min(currentHole, activeGame.totalHoles) }, (_, i) => i + 1).map(h => {
              const winner = getSkinWinner(h);
              const winnerUser = winner ? activeGame.players.find(p => p.userId === winner) : null;
              return (
                <div key={h} className={`text-center p-1 rounded text-xs ${winner ? 'bg-[#16a34a] bg-opacity-20' : 'bg-gray-900'}`}>
                  <p className="text-gray-400">H{h}</p>
                  <p className="text-white font-bold">{winnerUser ? winnerUser.name : winner === null && holes[String(h)]?.scores && Object.values(holes[String(h)].scores).some(s => s > 0) ? 'Carry' : '-'}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeGame.gameType === 'nassau' && (
        <div className="bg-black rounded-lg border border-gray-700 p-4 mb-4">
          <h4 className="text-white font-bold mb-2">Nassau Standings</h4>
          {(() => {
            const standings = getNassauStandings();
            return (
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div><p className="text-gray-400">Front 9</p>{activeGame.players.map(p => <p key={p.userId} className="text-white">{p.name}: {standings.front[p.userId]}</p>)}</div>
                <div><p className="text-gray-400">Back 9</p>{activeGame.players.map(p => <p key={p.userId} className="text-white">{p.name}: {standings.back[p.userId]}</p>)}</div>
                <div><p className="text-gray-400">Overall</p>{activeGame.players.map(p => <p key={p.userId} className="text-white">{p.name}: {standings.overall[p.userId]}</p>)}</div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Running Totals */}
      <div className="bg-black rounded-lg border border-gray-700 p-4 mb-4">
        <h4 className="text-white font-bold mb-2">Standings</h4>
        <div className="space-y-2">
          {[...activeGame.players].sort((a, b) => getTotalScore(a.userId) - getTotalScore(b.userId)).map((player, idx) => {
            const total = getTotalScore(player.userId);
            const totalPar = getTotalPar();
            const diff = total - totalPar;
            return (
              <div key={player.userId} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-400 text-sm w-6">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}.`}</span>
                  <span className="text-white">{player.name}</span>
                </div>
                <span className={`font-bold ${total === 0 ? 'text-gray-500' : diff <= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {total > 0 ? total : '-'} {total > 0 && `(${diff >= 0 ? '+' : ''}${diff})`}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pause Game - solo scorecards only */}
      {isOwner && activeGame.source === 'scorecard' && (
        <button onClick={handlePauseGame} className="w-full py-3 bg-gray-700 text-white rounded-lg font-bold hover:bg-gray-600 mb-2">
          Pause Game
        </button>
      )}
      {/* Complete Game - only owner */}
      {isOwner && (
        <button onClick={handleCompleteGame} className="w-full py-4 bg-[#16a34a] text-pure-white rounded-lg font-bold text-lg hover:bg-[#15803d]">
          Complete Game
        </button>
      )}
      {!isOwner && (
        <p className="text-center text-gray-500 text-sm mt-2">You can edit your own scores. Only the game creator can complete the game.</p>
      )}
    </div>
  );
};

export default LiveScoreView;
